




















import { defineComponent } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { $t } from "@/lib/i18nHelpers";
import { printPaymentPackage } from "@/lib/moneyHelpers";
import config from "@/config";
import { FormFields, FormValues, ListFields } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";

export default defineComponent({
  name: "PriceSettingsView",
  components: { BasePage, PageTitle, BaseForm, BaseButton },
  props: {
    loading: { type: Boolean, required: true },
    callPaymentPackages: { type: Array as () => PaymentPackage[], required: true },
    messagePaymentPackages: { type: Array as () => PaymentPackage[], required: true },
    values: { type: Object as () => FormValues, required: true },
  },
  model: {
    event: "input",
    prop: "values",
  },
  data() {
    return {
      saving: false as boolean,
    };
  },
  computed: {
    checkedCallOptionsAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyCallOptions"]);
    },
    checkedCallAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyCallPrice"]);
    },
    checkedMessageAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyMessagePrice"]);
    },
    perMinuteCallPrices(): ListFields {
      return [
        { value: "0", text: $t("Free") },
        ...this.callPaymentPackages.map(item => ({ value: item.id, text: printPaymentPackage(item) })),
      ];
    },
    perSentMessagePrices(): ListFields {
      return [
        { value: "0", text: $t("Free") },
        ...this.messagePaymentPackages.map(item => ({ value: item.id, text: printPaymentPackage(item) })),
      ];
    },
    freeMessageLimitOptions(): ListFields {
      return [
        { value: 0, text: $t("Disabled") },
        ...Array.from(new Array(20)).map((_, index) => ({ value: index + 1, text: index + 1 + "" })),
      ];
    },
    callfields(): FormFields {
      const fields: FormFields = [];
      if (config.featureCalling) {
        if (this.checkedCallOptionsAuthorities) {
          fields.push({
            class: "__call-enabling",
            name: "incomingCallsEnabled",
            label: $t("Enable incoming calls"),
            type: "switch",
          });
        }
        if ((this.values.incomingCallsEnabled || !this.checkedCallOptionsAuthorities) && this.checkedCallAuthorities) {
          fields.push({
            class: "__call-price",
            name: "callPricePackageId",
            label: $t("Price per minute of call"),
            type: "select",
            options: this.perMinuteCallPrices,
          });
        }
      }
      return fields;
    },
    messageFields(): FormFields {
      const fields: FormFields = [];
      if (config.featurePaidSentMessage && this.checkedMessageAuthorities) {
        fields.push(
          {
            class: "__per-message-subscriber",
            name: "subscriberPerSentPriceId",
            label: $t("Message for subs"),
            type: "select",
            options: this.perSentMessagePrices,
          },
          {
            class: "__per-message-attachment-subscriber",
            name: "subscriberPerSentWithAttachmentPriceId",
            label: $t("Media message for subs"),
            type: "select",
            options: this.perSentMessagePrices,
          },
          {
            class: "__per-message",
            fieldClass: this.values.messagePricePackageId !== "0" ? "__short mr-3" : "",
            name: "messagePricePackageId",
            label: $t("Message for nonsubs"),
            type: "select",
            options: this.perSentMessagePrices,
          },
          {
            class: "__free-message-limit",
            fieldClass: this.values.messagePricePackageId !== "0" ? "__short" : "hidden",
            name: "freeMessageLimit",
            label: $t("Free message limit"),
            type: "select",
            options: this.freeMessageLimitOptions,
          },
          {
            class: "__per-message-attachment",
            name: "perSentWithAttachmentPriceId",
            label: $t("Media message for nonsubs"),
            type: "select",
            options: this.perSentMessagePrices,
          }
        );
      }
      return fields;
    },
  },
  methods: {
    findAmountById(id: string): number {
      return this.messagePaymentPackages.find(p => p.id === id)?.amount ?? 0;
    },
    onInput(values: FormValues) {
      const perMessagePrice = this.findAmountById(values.messagePricePackageId);
      const perMediaMessagePrice = this.findAmountById(values.perSentWithAttachmentPriceId);
      const subscriberPerSentPrice = this.findAmountById(values.subscriberPerSentPriceId);
      const subscriberPerMediaMessagePrice = this.findAmountById(values.subscriberPerSentWithAttachmentPriceId);

      if (
        this.values.messagePricePackageId !== values.messagePricePackageId &&
        perMessagePrice > perMediaMessagePrice
      ) {
        values.perSentWithAttachmentPriceId = values.messagePricePackageId;
      } else if (
        this.values.perSentWithAttachmentPriceId !== values.perSentWithAttachmentPriceId &&
        perMediaMessagePrice < perMessagePrice
      ) {
        values.messagePricePackageId = values.perSentWithAttachmentPriceId;
      }

      if (
        this.values.subscriberPerSentPriceId !== values.subscriberPerSentPriceId &&
        subscriberPerSentPrice > subscriberPerMediaMessagePrice
      ) {
        values.subscriberPerSentWithAttachmentPriceId = values.subscriberPerSentPriceId;
      } else if (
        this.values.subscriberPerSentWithAttachmentPriceId !== values.subscriberPerSentWithAttachmentPriceId &&
        subscriberPerMediaMessagePrice < subscriberPerSentPrice
      ) {
        values.subscriberPerSentPriceId = values.subscriberPerSentWithAttachmentPriceId;
      }
      if (this.findAmountById(values.messagePricePackageId) === 0) values.freeMessageLimit = 0;

      this.$emit("input", values);
    },
    onSave() {
      this.saving = true;
      this.$emit("submit", () => (this.saving = false));
    },
  },
});
